"use client";

import { cn, invariant } from "@/lib/utils";
import { useRouterNoRender } from "@/ui/hooks/use-router-no-render";
import { MainProductImage } from "@/ui/storefront/products/main-product-image";
import { YnsLink } from "@/ui/yns-link";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from "lucide-react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import {
	type ComponentProps,
	Fragment,
	type ReactNode,
	createContext,
	startTransition,
	use,
	useEffect,
	useOptimistic,
} from "react";

type ImageModalProps = {
	images: string[];
	alt: string;
};

const ImageElement = ({ src, alt }: { src: string; alt: string }) => {
	return <MainProductImage src={src} alt={alt} className="h-full w-auto object-contain mx-auto" />;
};

export function ProductImageModal({ images, alt }: ImageModalProps) {
	const { imageIndex, setImageIndex } = useProductDetailsImageModalContext();

	const changeImageIndex = (index: number | null) => {
		if (imageIndex === index) {
			return;
		}

		setImageIndex(index);
	};

	console.log({ imageIndex });

	const src = imageIndex !== null && images[Number(imageIndex)];

	const onDismiss = () => {
		changeImageIndex(null);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: onDismiss is a constant function
	useEffect(() => {
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				onDismiss();
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, []);

	if (!src) {
		return null;
	}

	const handlePrevious = () => {
		const pos = images.indexOf(src);
		if (pos >= 0) {
			const newPos = (images.indexOf(src) - 1 + images.length) % images.length;
			changeImageIndex(newPos);
		}
	};

	const handleNext = () => {
		const pos = images.indexOf(src);
		if (pos <= images.length - 1) {
			const newPos = (images.indexOf(src) + 1) % images.length;
			changeImageIndex(newPos);
		}
	};

	return (
		<>
			{/* preload images */}
			<div className="absolute pointer-events-none opacity-0 -z-50">
				<ImageElement src={src} alt={alt} />
			</div>
			<DialogPrimitive.Root open={true} modal>
				<DialogPrimitive.Portal>
					<DialogPrimitive.Overlay />
					<DialogPrimitive.Content
						className={cn("fixed inset-0 z-50 bg-neutral-100 flex flex-col animate-in fade-in")}
					>
						<DialogPrimitive.Title className="sr-only">{alt}</DialogPrimitive.Title>
						<DialogPrimitive.Description className="sr-only" />
						<button
							type="button"
							onClick={() => onDismiss()}
							className="ml-auto text-neutral-500 hover:text-neutral-700 p-2"
						>
							<XIcon className="w-6 h-6" />
						</button>

						<div className="grow flex items-center justify-center overflow-hidden">
							<div key={src} className="relative w-full h-full animate-in fade-in">
								<ImageElement src={src} alt={alt} />
							</div>

							<button
								type="button"
								onClick={() => {
									handlePrevious();
								}}
								className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 hover:bg-gray-100 transition-colors"
							>
								<ChevronLeftIcon className="w-6 h-6" />
							</button>
							<button
								type="button"
								onClick={() => {
									handleNext();
								}}
								className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 hover:bg-gray-100 transition-colors"
							>
								<ChevronRightIcon className="w-6 h-6" />
							</button>
						</div>

						<div className="flex justify-center gap-4 py-2">
							{images.map((image, idx) => {
								return (
									<Fragment key={idx}>
										<ProductDetailsImageLink
											className={cn(
												"border-transparent border rounded-lg overflow-hidden",
												src === image && "border-black",
											)}
											onClick={(e) => {
												e.preventDefault();
												changeImageIndex(idx);
											}}
											key={idx}
											prefetch={true}
											scroll={false}
											image={idx}
										>
											<Image
												src={image}
												alt={""}
												width={80}
												height={80}
												className="object-cover h-auto w-auto"
												sizes="80px"
											/>
										</ProductDetailsImageLink>
									</Fragment>
								);
							})}
						</div>
					</DialogPrimitive.Content>
				</DialogPrimitive.Portal>
			</DialogPrimitive.Root>
		</>
	);
}

const ProductDetailsImageModalContext = createContext<{
	imageIndex: number | null;
	setImageIndex: (imageIndex: number | null) => void;
} | null>(null);

export const ProductDetailsImageModalProvider = ({ children }: { children: ReactNode }) => {
	const searchParams = useSearchParams();
	const spImage = searchParams.get("image");
	const idx = spImage ? Number.parseInt(spImage, 10) : null;
	const [imageIndex, setOptimisticImageIndex] = useOptimistic(Number.isFinite(idx) ? idx : null);
	const routerNoRender = useRouterNoRender();

	const setImageIndex = (imageIndex: number | null) => {
		startTransition(() => {
			setOptimisticImageIndex(imageIndex);

			const params = new URLSearchParams(searchParams);
			if (imageIndex !== null) {
				params.set("image", imageIndex.toString());
			} else {
				params.delete("image");
			}
			routerNoRender.replace(`?${params}`);
		});
	};

	return (
		<ProductDetailsImageModalContext value={{ imageIndex, setImageIndex }}>
			{children}
		</ProductDetailsImageModalContext>
	);
};

export const useProductDetailsImageModalContext = () => {
	const ctx = use(ProductDetailsImageModalContext);
	invariant(ctx, "useProductDetailsImageModalContext must be used within a ProductDetailsImageModalProvider");
	return ctx;
};

export const ProductDetailsImageLink = ({
	image,
	...props
}: Omit<ComponentProps<typeof YnsLink>, "href"> & { image: number }) => {
	const ctx = useProductDetailsImageModalContext();
	const searchParams = useSearchParams();
	const newParams = new URLSearchParams(searchParams);
	newParams.set("image", image.toString());

	return (
		<YnsLink
			{...props}
			href={`?${newParams}`}
			onClick={(e) => {
				e.preventDefault();
				ctx.setImageIndex(image);
			}}
		/>
	);
};
